import { Button } from '@mui/material';
import { PersonSearch, Mail, Preview, AddTask } from '@mui/icons-material';
import { ControlRecord, ShowProfileProps } from '../ts/Definitions';
import { OwnerType, ProfileRole } from '../ts/enums';
import { blue, orange } from '@mui/material/colors';
import Emitter, { ShowProfile, ShowSessionDetails } from '../utilities/Emitter';
import ProfileControl from '../controls/ProfileControl';
import { formatValidDate } from '../utilities/dateUtils';
import { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { ControllerContext } from '../ts/Contexts';
import { MessageType } from '../controllers/messaging';


interface Props {
    profile: ControlRecord;
    assignTaskCallback: (profile: ControlRecord) => void;
}

export default function QuickTaskProfile({ profile, assignTaskCallback
}: Props): JSX.Element {
    const [blinkingBackgroundColour, setBlinkingBackgroundColour] = useState<string>('');
    const timerHandle = useRef<NodeJS.Timer>();
    const blinkingBackgroundColourRef = useRef<string>(blinkingBackgroundColour);
    blinkingBackgroundColourRef.current = blinkingBackgroundColour;
    const controller = useContext(ControllerContext);
    const editorSettings = controller.getLoggedOnUserSettings();
    const [showProfileProps, setShowProfileProps] = useState<ShowProfileProps>({});

    const refreshStateData = useCallback(() => {
        setShowProfileProps(new ShowProfileProps(profile, editorSettings));
    }, [editorSettings, profile]);

    useEffect(() => {
        if (!editorSettings) return;
        if (editorSettings.getOwnedSessions().length === 0) timerHandle.current = setInterval(() => setBlinkingBackgroundColour(!blinkingBackgroundColourRef.current ? orange[500] : ''), 1000)
        editorSettings.subscribeToViewChanges(refreshStateData);
        editorSettings.subscribeToControlRecord(refreshStateData);
        refreshStateData();
        return () => {
            if (timerHandle.current) clearInterval(timerHandle.current);
            if (!editorSettings) return;
            editorSettings.unsubscribeToViewChanges(refreshStateData);
            editorSettings.unsubscribeToControlRecord(refreshStateData);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorSettings, refreshStateData]);

    // show attribute if hidden
    return (
        <div className={'settingItem'} style={{ paddingBottom: 5 }}>
            <ProfileControl
                profile={profile}
                loggedOnUser={editorSettings.isSystemLoggedOn() || editorSettings.getControl().userid === profile.userid}
                updatedProfile={(profile) => editorSettings.updateControlAttributes(profile)}>
                <span className="itemDescription" >
                    {profile.role !== ProfileRole.None && (<span><span className='header'>Role:</span> <span className='shopItemTags'>{ProfileRole[profile.role as any as ProfileRole]}</span></span>)}
                    {profile.role !== ProfileRole.Owner && (<div><span>Owned Status: <span className='shopItemTags'>{OwnerType[profile.getOwnerType()]}</span></span></div>)}
                    <div><span>Last Active: <span className='shopItemTags'>{formatValidDate(profile.lastUpdateTime, 'dd MMM yyyy HH:mm')}</span></span></div>
                </span>
            </ProfileControl>
            <div className='gridActionButtons' style={{ paddingRight: 10 }}>
                <div>
                    <Button className="smallButton" title="Assign a task to this member"
                        style={{ background: blinkingBackgroundColour }}
                        onClick={(args) => {
                            assignTaskCallback(profile);
                            args.preventDefault();
                            args.stopPropagation();
                        }}>Assign Task<AddTask fontSize='small' /></Button>
                </div>
                <div>
                    <Button className="smallButton" title="Send member message" onClick={(args) => {
                        Emitter.emitMessage({ toUserid: profile.userid, toUsername: profile.username, messageType: MessageType.MemberMessage });
                        args.preventDefault();
                        args.stopPropagation();
                    }}>Message<Mail fontSize='small' /></Button>
                </div>
                <div>
                    <Button className="smallButton" title="View member profile" onClick={(args) => {
                        Emitter.emit(ShowProfile, showProfileProps);
                        args.preventDefault();
                        args.stopPropagation();
                    }}>View Profile<PersonSearch fontSize='small' /></Button>
                </div>
                <div>
                    <Button className="smallButton" style={{ color: blue[900] }} title="View member session" onClick={(args) => {
                        Emitter.emit(ShowSessionDetails, profile.userid);
                        args.preventDefault();
                        args.stopPropagation();
                    }}>View Session<Preview fontSize='small' /></Button>
                </div>
            </div>
        </div>
    )
}

